import { useState, useEffect } from 'react';
import { useUser } from '../../context/userContext';
import { FiCheckCircle, FiAlertCircle, FiArrowLeft, FiX, FiInfo } from 'react-icons/fi';
import { db } from '../../firebase/firestore';
import { collection, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Footer from '../Footer/Footer';

export default function WithdrawForm() {
  const { 
    adsBalance,
    id,
    username,
    fullName,
    loading,
    setAdsBalance,
  } = useUser();

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    amount: '',
    walletAddress: '',
    network: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [withdrawalFee, setWithdrawalFee] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [showSuccessReceipt, setShowSuccessReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  
  const MIN_WITHDRAWAL = 1000;
  const FEE_PERCENTAGE = 1;

  const networks = [
    { id: 'SHIB', name: 'Shibainu', icon: '/ltc-icon.png' },
  ];

  useEffect(() => {
    if (formData.amount && !isNaN(parseFloat(formData.amount))) {
      const amountNum = parseFloat(formData.amount);
      const fee = (amountNum * FEE_PERCENTAGE) / 100;
      setWithdrawalFee(fee);
      setTotalAmount(amountNum + fee);
    } else {
      setWithdrawalFee(0);
      setTotalAmount(0);
    }
  }, [formData.amount]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.amount) return setError('Please enter an amount');
    
    const amountNum = parseFloat(formData.amount);
    if (isNaN(amountNum)) return setError('Please enter a valid amount');
    if (amountNum < MIN_WITHDRAWAL) return setError(`Minimum withdrawal is $SHIB${MIN_WITHDRAWAL}`);
    if (totalAmount > adsBalance) return setError(`Insufficient balance (including fee)`);
    if (!formData.walletAddress) return setError('Please enter your wallet address');
    if (!formData.network) return setError('Please select a network');

    try {
      // Update user balance
      const userRef = doc(db, 'telegramUsers', id);
      await updateDoc(userRef, {
        adsBalance: adsBalance - totalAmount
      });

      // Create withdrawal record
      const withdrawalRef = collection(db, 'withdrawalRequests');
      const withdrawalData = {
        userId: id,
        username: username || '',
        fullName: fullName || '',
        amount: amountNum,
        fee: withdrawalFee,
        totalDeducted: totalAmount,
        walletAddress: formData.walletAddress.trim(),
        network: formData.network,
        status: 'pending',
        createdAt: serverTimestamp(),
        balanceType: 'ads'
      };
      
      const docRef = await addDoc(withdrawalRef, withdrawalData);

      // Update UI state
      setAdsBalance(prev => prev - totalAmount);
      setSuccess('Withdrawal request submitted!');
      
      // Prepare receipt data
      setReceiptData({
        id: docRef.id,
        amount: amountNum.toFixed(3),
        fee: withdrawalFee.toFixed(3),
        total: totalAmount.toFixed(3),
        wallet: formData.walletAddress.trim(),
        network: networks.find(n => n.id === formData.network)?.name || formData.network,
        date: new Date().toLocaleString()
      });
      setShowSuccessReceipt(true);
      setFormData({ amount: '', walletAddress: '', network: '' });
      
    } catch (err) {
      console.error('Withdrawal error:', err);
      setError(err.message || 'Failed to process withdrawal');
    }
  };

  const handleMaxClick = () => {
    const maxAmount = adsBalance / (1 + (FEE_PERCENTAGE / 100));
    setFormData(prev => ({
      ...prev,
      amount: maxAmount.toFixed(3)
    }));
  };

  const isSubmitDisabled = loading || !formData.amount || 
                         parseFloat(formData.amount) < MIN_WITHDRAWAL || 
                         totalAmount > adsBalance ||
                         !formData.walletAddress || 
                         !formData.network;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 px-4 py-6 max-w-md mx-auto w-full">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-white shadow-sm hover:bg-gray-100 transition-colors"
          >
            <FiArrowLeft className="text-gray-600" size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 ml-4">Withdraw Funds</h1>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          {/* Balance Info */}
          <div className="flex justify-between items-center mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-600">Available Balance</p>
              <p className="text-2xl font-bold text-gray-900">$SHIB{adsBalance.toFixed(3)}</p>
            </div>
            <div className="text-blue-500">
              <FiInfo size={20} />
            </div>
          </div>

          {/* Withdrawal Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount (USD)</label>
              <div className="flex border rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                <input
                  type="number"
                  name="amount"
                  placeholder="0.000"
                  value={formData.amount}
                  onChange={handleChange}
                  className="flex-1 px-4 py-3 text-gray-900 focus:outline-none"
                  step="0.001"
                  min={MIN_WITHDRAWAL}
                  max={adsBalance}
                />
                <button 
                  type="button" 
                  onClick={handleMaxClick}
                  className="px-4 text-blue-600 font-medium hover:bg-gray-50 transition-colors"
                  disabled={!adsBalance || adsBalance < MIN_WITHDRAWAL}
                >
                  MAX
                </button>
              </div>
              {formData.amount && parseFloat(formData.amount) < MIN_WITHDRAWAL && (
                <p className="mt-2 text-sm text-red-600">Minimum withdrawal is $SHIB{MIN_WITHDRAWAL}</p>
              )}
              {formData.amount && totalAmount > adsBalance && (
                <p className="mt-2 text-sm text-red-600">Insufficient balance (including fee)</p>
              )}
            </div>

            {/* Fee Breakdown */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Amount:</span>
                <span className="font-medium">$SHIB{formData.amount ? parseFloat(formData.amount).toFixed(3) : '0.000'}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Fee ({FEE_PERCENTAGE}%):</span>
                <span className="font-medium">$SHIB{withdrawalFee.toFixed(3)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-800 font-semibold">Total:</span>
                <span className="text-gray-900 font-bold">$SHIB{totalAmount.toFixed(3)}</span>
              </div>
            </div>

            {/* Wallet Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Wallet Address</label>
              <input
                type="text"
                name="walletAddress"
                placeholder="Enter wallet address"
                value={formData.walletAddress}
                onChange={handleChange}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Network Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Network</label>
              <select
                name="network"
                value={formData.network}
                onChange={handleChange}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none appearance-none"
              >
                <option value="">Select network</option>
                {networks.map(network => (
                  <option key={network.id} value={network.id}>
                    <div className="flex items-center">
                      {network.icon && (
                        <img src={network.icon} alt={network.name} className="w-5 h-5 mr-2" />
                      )}
                      {network.name} ({network.id})
                    </div>
                  </option>
                ))}
              </select>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="flex items-center p-4 bg-red-50 text-red-600 rounded-lg">
                <FiAlertCircle className="mr-2" />
                {error}
              </div>
            )}

            {success && (
              <div className="flex items-center p-4 bg-green-50 text-green-600 rounded-lg">
                <FiCheckCircle className="mr-2" />
                {success}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className={`w-full py-3 px-6 rounded-xl font-bold text-white transition-colors ${
                isSubmitDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                  Processing...
                </span>
              ) : (
                'Request Withdrawal'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Success Receipt Modal */}
      {showSuccessReceipt && receiptData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full overflow-hidden shadow-xl">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-xl font-bold text-gray-900">Withdrawal Receipt</h3>
              <button 
                onClick={() => setShowSuccessReceipt(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX size={24} />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Transaction ID:</span>
                <span className="font-medium text-right max-w-xs break-all">
                  {receiptData.id}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className="flex items-center text-green-600 font-semibold">
                  <FiCheckCircle className="mr-2" />
                  Success
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-medium">$SHIB{receiptData.amount}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Fee:</span>
                <span className="font-medium">$SHIB{receiptData.fee}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Total Deducted:</span>
                <span className="font-medium">$SHIB{receiptData.total}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Network:</span>
                <span className="font-medium">{receiptData.network}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Wallet:</span>
                <span className="font-medium text-right max-w-xs break-all">
                  {receiptData.wallet}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Date:</span>
                <span className="font-medium">{receiptData.date}</span>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t flex justify-center">
              <button 
                onClick={() => setShowSuccessReceipt(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}
